import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type {
  MonitoredCallSummary,
  MonitoredCallsQuery,
  RemoteCallOrigin,
  RemoteCallOutcome,
  RemoteCallStatus,
  RemoteSourceInfo,
  SourceOverview,
} from "@shared/types";
import { MONITOR_ENVIRONMENT_LABELS, REMOTE_OUTCOME_LABELS } from "@shared/types";
import { ApiError } from "../../lib/api";
import {
  formatDuration,
  formatWhen,
  getOverview,
  listSourceCalls,
  PERIOD_LABELS,
  prettyPhone,
  relativeTime,
  shortRevision,
  sinceFor,
  type Period,
} from "../../lib/monitoring";
import { Button } from "../../components/ui/button";
import { Select } from "../../components/ui/input";
import { EnvBadge, HealthBadge, InternalTestBadge, OriginBadge, ResultBadge } from "../../components/supervision/badges";

type OriginFilter = "all" | RemoteCallOrigin;
type ResultFilter = "all" | RemoteCallOutcome | "manque" | "en_cours";

const PAGE_SIZE = 50;

const RESULT_OPTIONS: Array<{ value: ResultFilter; label: string }> = [
  { value: "all", label: "Tous les résultats" },
  ...(Object.keys(REMOTE_OUTCOME_LABELS) as RemoteCallOutcome[]).map((o) => ({ value: o, label: REMOTE_OUTCOME_LABELS[o] })),
  { value: "manque", label: "Manqués" },
  { value: "en_cours", label: "En cours" },
];

/** Production avant préproduction, puis par client : l'ordre des cartes est stable. */
function sortOverview(list: SourceOverview[]): SourceOverview[] {
  return [...list].sort((a, b) => {
    if (a.source.position !== b.source.position) return a.source.position - b.source.position;
    if (a.source.clientName !== b.source.clientName) return a.source.clientName.localeCompare(b.source.clientName, "fr");
    return a.source.environment === b.source.environment ? 0 : a.source.environment === "prod" ? -1 : 1;
  });
}

/**
 * Conversations des clients : une carte par source (client × environnement),
 * puis l'historique de la source choisie. La production (clients réels) et la
 * préproduction (tests) ne se mélangent jamais dans une même liste ; chaque
 * appel porte en plus son origine (téléphone ou simulation) et, si le numéro
 * est un testeur déclaré, la mention « test interne ».
 */
export function SupervisionPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState<SourceOverview[] | null>(null);
  const [overviewFailed, setOverviewFailed] = useState(false);

  const [period, setPeriod] = useState<Period>("7d");
  const [origin, setOrigin] = useState<OriginFilter>("all");
  const [result, setResult] = useState<ResultFilter>("all");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  const [calls, setCalls] = useState<MonitoredCallSummary[]>([]);
  const [remote, setRemote] = useState<RemoteSourceInfo | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadOverview = useCallback(() => {
    getOverview()
      .then((list) => {
        setOverview(sortOverview(list));
        setOverviewFailed(false);
      })
      .catch(() => {
        setOverview([]);
        setOverviewFailed(true);
      });
  }, []);

  useEffect(loadOverview, [loadOverview]);

  // Source active : celle de l'URL si elle existe, sinon la première (production d'abord).
  const selectedId = params.get("source");
  const selected = useMemo(() => {
    if (!overview) return null;
    return overview.find((o) => o.source.id === selectedId) ?? overview.find((o) => o.source.enabled) ?? overview[0] ?? null;
  }, [overview, selectedId]);

  useEffect(() => {
    if (selected && selected.source.id !== selectedId) {
      const next = new URLSearchParams(params);
      next.set("source", selected.source.id);
      setParams(next, { replace: true });
    }
  }, [selected, selectedId, params, setParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const baseQuery = useMemo((): MonitoredCallsQuery => {
    const q: MonitoredCallsQuery = { since: sinceFor(period), limit: PAGE_SIZE };
    if (origin !== "all") q.origin = origin;
    if (result === "manque" || result === "en_cours") q.status = [result as RemoteCallStatus];
    else if (result !== "all") q.outcome = [result];
    if (debounced) q.q = debounced;
    return q;
  }, [period, origin, result, debounced]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listSourceCalls(selected.source.id, baseQuery)
      .then((page) => {
        if (cancelled) return;
        setCalls(page.calls);
        setRemote(page.remote);
        setHasMore(page.hasMore);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCalls([]);
        setRemote(null);
        setHasMore(false);
        setError(describeError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, baseQuery, reloadKey]);

  const loadMore = async () => {
    if (!selected || loadingMore || calls.length === 0) return;
    setLoadingMore(true);
    try {
      const last = calls[calls.length - 1]!;
      const page = await listSourceCalls(selected.source.id, { ...baseQuery, before: last.startedAt });
      setCalls((prev) => [...prev, ...page.calls.filter((c) => !prev.some((p) => p.id === c.id))]);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  const refresh = () => {
    loadOverview();
    setReloadKey((k) => k + 1);
  };

  const selectSource = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("source", id);
    setParams(next);
    setCalls([]);
  };

  const env = selected?.source.environment ?? "prod";
  const mismatch = selected?.health.environmentMismatch ?? false;

  return (
    <div className={`supervision supervision--${env}`}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Conversations clients</h1>
          <p className="page-header__subtitle">
            Ce que les assistants ont dit au téléphone chez chaque client, relu en direct depuis son portail.
            La production montre les clients réels ; la préproduction, les tests.
          </p>
        </div>
        <div className="page-header__actions">
          <Button variant="ghost" size="sm" onClick={refresh} disabled={loading}>
            Actualiser
          </Button>
          <Link to="/supervision/sources" className="btn btn--ghost btn--sm">
            Gérer les sources
          </Link>
        </div>
      </header>

      {overview === null ? (
        <p className="supervision__empty">Chargement des sources…</p>
      ) : overview.length === 0 ? (
        <div className="supervision__onboarding">
          <h2>Aucune source de supervision</h2>
          <p>
            {overviewFailed
              ? "Les sources n'ont pas pu être lues. Réessayez dans un instant."
              : "Déclarez le portail d'un client (préproduction d'abord) avec le jeton généré dans ses réglages « Répondeur IA »."}
          </p>
          <Link to="/supervision/sources" className="btn btn--primary">
            Ajouter une source
          </Link>
        </div>
      ) : (
        <>
          <div className="src-cards" role="tablist" aria-label="Sources">
            {overview.map((o) => (
              <SourceCard
                key={o.source.id}
                overview={o}
                active={selected?.source.id === o.source.id}
                onSelect={() => selectSource(o.source.id)}
              />
            ))}
          </div>

          {selected ? (
            <>
              <div className={`env-strip env-strip--${env}`} role="status">
                <EnvBadge environment={env} />
                <span className="env-strip__who">
                  {selected.source.clientName}
                  {selected.source.phoneNumber ? (
                    <span className="env-strip__phone"> · ligne {prettyPhone(selected.source.phoneNumber)}</span>
                  ) : null}
                </span>
                <span className="env-strip__hint">
                  {env === "preprod"
                    ? "Appels d'essai : aucun client réel, aucune donnée de production."
                    : "Appels de clients réels : chaque conversation compte."}
                </span>
                {mismatch && selected.health.remote ? (
                  <span className="env-strip__alert" role="alert">
                    L'instance se déclare « {MONITOR_ENVIRONMENT_LABELS[selected.health.remote.environment]} » :
                    vérifiez l'adresse de cette source.
                  </span>
                ) : null}
              </div>

              <div className="sv-filters">
                <div className="sv-filters__group" role="group" aria-label="Origine">
                  {(
                    [
                      ["all", "Toutes origines"],
                      ["telephone", "Téléphone"],
                      ["simulation", "Simulations"],
                    ] as Array<[OriginFilter, string]>
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`sv-chip${origin === value ? " is-active" : ""}`}
                      aria-pressed={origin === value}
                      onClick={() => setOrigin(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <Select aria-label="Période" value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                  {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                    <option key={p} value={p}>
                      {PERIOD_LABELS[p]}
                    </option>
                  ))}
                </Select>
                <Select aria-label="Résultat" value={result} onChange={(e) => setResult(e.target.value as ResultFilter)}>
                  {RESULT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <input
                  type="search"
                  className="input sv-filters__search"
                  placeholder="Numéro, nom, véhicule, sujet…"
                  aria-label="Rechercher une conversation"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <span className="sv-filters__count">
                  {loading ? "…" : `${calls.length}${hasMore ? "+" : ""} conversation${calls.length > 1 ? "s" : ""}`}
                </span>
              </div>

              {error ? (
                <div className="sv-error" role="alert">
                  <strong>Historique indisponible.</strong> {error}
                </div>
              ) : null}

              <table className="table sv-table">
                <thead>
                  <tr>
                    <th>Quand</th>
                    <th>Appelant</th>
                    <th>Durée</th>
                    <th>Résultat</th>
                    <th>Origine</th>
                    <th>Moteur</th>
                    <th>Tours</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {calls.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="table__empty">
                        {loading
                          ? "Chargement…"
                          : error
                            ? "—"
                            : debounced
                              ? "Aucune conversation ne correspond."
                              : `Aucune conversation sur ${PERIOD_LABELS[period].toLowerCase()}.`}
                      </td>
                    </tr>
                  ) : (
                    calls.map((c) => (
                      <tr
                        key={c.id}
                        className={`table__row--link sv-row sv-row--${c.origin}${c.internalTest ? " sv-row--internal" : ""}`}
                        onClick={() => navigate(`/supervision/${selected.source.id}/appels/${encodeURIComponent(c.id)}`)}
                      >
                        <td>
                          <span className="sv-row__when" title={new Date(c.startedAt).toLocaleString("fr-CA")}>
                            {formatWhen(c.startedAt)}
                          </span>
                          <span className="sv-row__ago">{relativeTime(c.startedAt)}</span>
                        </td>
                        <td>
                          <span className="sv-row__phone">{prettyPhone(c.fromNumber)}</span>
                          {c.callerName ? <span className="sv-row__name">{c.callerName}</span> : null}
                          {c.intent ? <span className="sv-row__intent">{c.intent}</span> : null}
                        </td>
                        <td>{formatDuration(c.durationSec)}</td>
                        <td>
                          <span className="sv-row__badges">
                            <ResultBadge status={c.status} outcome={c.outcome} />
                            {c.handled ? <span className="sv-row__handled" title="Traité par l'équipe du client">✓</span> : null}
                          </span>
                        </td>
                        <td>
                          <span className="sv-row__badges">
                            <OriginBadge origin={c.origin} />
                            {c.internalTest ? <InternalTestBadge /> : null}
                          </span>
                        </td>
                        <td>
                          <EngineCell call={c} />
                        </td>
                        <td>
                          <TurnsCell call={c} />
                        </td>
                        <td className="table__actions">
                          <Link to={`/supervision/${selected.source.id}/appels/${encodeURIComponent(c.id)}`}>Lire</Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {hasMore ? (
                <div className="sv-more">
                  <Button variant="ghost" onClick={() => void loadMore()} disabled={loadingMore}>
                    {loadingMore ? "Chargement…" : "Conversations plus anciennes"}
                  </Button>
                </div>
              ) : null}

              {remote ? (
                <p className="sv-foot">
                  Lu depuis {selected.source.baseUrl} ({remote.client}, {MONITOR_ENVIRONMENT_LABELS[remote.environment].toLowerCase()})
                  {" "}le {new Date(remote.generatedAt).toLocaleString("fr-CA")}. Aucune copie conservée dans MC.
                </p>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function SourceCard({ overview, active, onSelect }: { overview: SourceOverview; active: boolean; onSelect: () => void }) {
  const { source, health } = overview;
  const stats = health.stats;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`src-card src-card--${source.environment}${active ? " is-active" : ""}${source.enabled ? "" : " is-disabled"}`}
      onClick={onSelect}
    >
      <span className="src-card__env">
        <EnvBadge environment={source.environment} />
      </span>
      <span className="src-card__client">{source.clientName}</span>
      <span className="src-card__phone">{source.phoneNumber ? prettyPhone(source.phoneNumber) : source.baseUrl.replace(/^https?:\/\//, "")}</span>
      {health.state === "ok" && stats ? (
        <span className="src-card__stats">
          <span>
            <strong>{stats.today}</strong> aujourd'hui
          </span>
          <span>
            <strong>{stats.week}</strong> cette semaine
          </span>
          {stats.inProgress > 0 ? (
            <span className="src-card__live">
              <strong>{stats.inProgress}</strong> en cours
            </span>
          ) : null}
        </span>
      ) : null}
      <span className="src-card__foot">
        <HealthBadge state={health.state} />
        <span className="src-card__last">
          {health.state === "ok"
            ? stats?.lastCallAt
              ? `Dernier appel ${relativeTime(stats.lastCallAt)}`
              : "Aucun appel enregistré"
            : health.message ?? ""}
        </span>
      </span>
      {health.environmentMismatch ? <span className="src-card__warn">Environnement déclaré différent</span> : null}
    </button>
  );
}

function EngineCell({ call }: { call: MonitoredCallSummary }) {
  const p = call.provenance;
  if (!p) return <span className="sv-dim">non transmis</span>;
  const engine = p.engine ?? "inconnu";
  return (
    <span className="sv-engine" title={[p.appRevision && `application ${p.appRevision}`, p.engineRevision && `moteur ${p.engineRevision}`].filter(Boolean).join("\n")}>
      <span className={`sv-engine__name sv-engine__name--${engine}`}>{engine}</span>
      <span className="sv-engine__rev">{shortRevision(p.appRevision)}</span>
    </span>
  );
}

function TurnsCell({ call }: { call: MonitoredCallSummary }) {
  const p = call.provenance;
  const prepared = p?.preparedTurns ?? null;
  const model = p?.modelCalls ?? null;
  // Le relais historique ne compte pas ses tours : « 0/0 » y serait un mensonge.
  const counted = prepared != null && (prepared > 0 || (model ?? 0) > 0 || p?.engine === "blueprint");
  return (
    <span className="sv-turns" title={counted ? `${prepared} tour(s) sans modèle, ${model ?? 0} requête(s) modèle` : undefined}>
      <span>{call.turns}</span>
      {counted ? (
        <span className="sv-turns__split">
          <span className="sv-turns__prepared">{prepared}</span>/<span className="sv-turns__model">{model ?? 0}</span>
        </span>
      ) : null}
    </span>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "remote_unreachable":
        return "Le portail du client ne répond pas.";
      case "remote_unauthorized":
        return "Le portail refuse le jeton : régénérez-le dans ses réglages et mettez la source à jour.";
      case "remote_not_configured":
        return "Aucun jeton de supervision n'est configuré côté client.";
      case "remote_bad_contract":
        return "Le portail répond dans un format inattendu (version de contrat différente ?).";
      default:
        return `Erreur ${err.status}.`;
    }
  }
  return "Erreur inattendue.";
}
